/**
 * SDR Stock Manager - Reactive Central In-Memory Store
 * @module state/store
 */

import { DEFAULT_SETTINGS } from '../config/constants.js';

/**
 * Creates a fresh default application state object.
 * @returns {import('../types/index.js').AppState}
 */
export function createInitialState() {
  return {
    inventory: [],
    transactions: [],
    stockAudits: [],
    stockOutMaster: [],
    stockOutDetails: [],
    activeAuditId: null,
    settings: { ...DEFAULT_SETTINGS },
    theme: 'light',
    activeTab: 'inventory',
    filter: 'all',
    filterDate: '',
    locationFilter: 'all',
    searchQuery: '',
    activeViewingId: null,
    selectedInventoryIds: new Set(),
    sortColumn: 'name',
    sortDirection: 'asc',
    lastCloudSyncAt: ''
  };
}

/**
 * Internal reactive state singleton.
 * @type {import('../types/index.js').AppState}
 */
let state = createInitialState();

/**
 * Subscription listeners set.
 * @type {Set<(state: import('../types/index.js').AppState, changedKeys: string[]) => void>}
 */
const listeners = new Set();

// Concurrency locks & state flags
let stockMutationInProgress = false;
let itemSaveInProgress = false;
let cloudSyncInProgress = false;
let cloudSyncQueued = false;
let realtimeSelfPauseUntil = 0;
let isInventoryDirty = true;
let isTransactionsDirty = true;
let isAuditDirty = true;
let isStockOutDirty = true;

/**
 * Retrieves the current application state reference.
 * @returns {import('../types/index.js').AppState}
 */
export function getState() {
  return state;
}

/**
 * Subscribes a listener function to state changes.
 * @param {(state: import('../types/index.js').AppState, changedKeys: string[]) => void} listener
 * @returns {() => void} Unsubscribe callback
 */
export function subscribe(listener) {
  if (typeof listener !== 'function') return () => {};
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Notifies all active subscribers of state changes.
 * @param {string[]} [changedKeys=[]]
 */
export function notifyListeners(changedKeys = []) {
  const keys = Array.isArray(changedKeys) ? changedKeys : [changedKeys];
  listeners.forEach(listener => {
    try {
      listener(state, keys);
    } catch (err) {
      console.error('Error in store listener:', err);
    }
  });
}

/**
 * Updates partial state and dispatches change notification.
 * @param {Partial<import('../types/index.js').AppState>} partialState
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function setState(partialState, options = {}) {
  if (!partialState || typeof partialState !== 'object') return;
  const changedKeys = [];

  for (const key of Object.keys(partialState)) {
    if (key in state) {
      if (key === 'settings' && partialState.settings) {
        state.settings = { ...state.settings, ...partialState.settings };
      } else {
        state[key] = partialState[key];
      }
      changedKeys.push(key);
    }
  }

  if (changedKeys.includes('inventory')) {
    isInventoryDirty = true;
  }
  if (changedKeys.includes('transactions')) {
    isTransactionsDirty = true;
  }
  if (changedKeys.includes('stockAudits')) {
    isAuditDirty = true;
  }
  if (changedKeys.includes('stockOutMaster') || changedKeys.includes('stockOutDetails')) {
    isStockOutDirty = true;
  }

  if (!options.silent && changedKeys.length > 0) {
    notifyListeners(changedKeys);
  }
}

/**
 * Resets state to default or provided custom state.
 * @param {Partial<import('../types/index.js').AppState>} [customState]
 */
export function resetState(customState) {
  const fresh = createInitialState();
  if (customState && typeof customState === 'object') {
    Object.assign(fresh, customState);
    if (customState.settings) {
      fresh.settings = { ...DEFAULT_SETTINGS, ...customState.settings };
    }
  }
  state = fresh;
  isInventoryDirty = true;
  isTransactionsDirty = true;
  isAuditDirty = true;
  isStockOutDirty = true;
  notifyListeners(Object.keys(state));
}

// ============================================================================
// Granular Mutators
// ============================================================================

/**
 * Inserts or updates an inventory item lot in state.
 * @param {import('../types/index.js').InventoryItem} item
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function updateInventoryItem(item, options = {}) {
  if (!item || !item.id) return;
  const normalizedItem = {
    ...item,
    quantity: Number(item.quantity) || 0,
    deleted: Boolean(item.deleted),
    updatedAt: item.updatedAt || new Date().toISOString()
  };

  const index = state.inventory.findIndex(i => i.id === normalizedItem.id);
  if (index >= 0) {
    state.inventory[index] = { ...state.inventory[index], ...normalizedItem };
  } else {
    state.inventory.push(normalizedItem);
  }

  isInventoryDirty = true;
  if (!options.silent) {
    notifyListeners(['inventory']);
  }
}

/**
 * Marks an inventory item lot as deleted (tombstone).
 * @param {string} itemId
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function removeInventoryItem(itemId, options = {}) {
  if (!itemId) return;
  const item = state.inventory.find(i => i.id === itemId);
  if (item) {
    item.deleted = true;
    item.updatedAt = new Date().toISOString();
    isInventoryDirty = true;
    if (!options.silent) {
      notifyListeners(['inventory']);
    }
  }
}

/**
 * Adds a new transaction record to state.
 * @param {import('../types/index.js').Transaction} transaction
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function addTransaction(transaction, options = {}) {
  if (!transaction || !transaction.id) return;
  const normalized = {
    ...transaction,
    quantity: Number(transaction.quantity) || 0,
    deleted: Boolean(transaction.deleted),
    updatedAt: transaction.updatedAt || transaction.timestamp || new Date().toISOString()
  };

  state.transactions.unshift(normalized);
  if (!options.silent) {
    notifyListeners(['transactions']);
  }
}

/**
 * Updates an existing transaction in state.
 * @param {import('../types/index.js').Transaction} transaction
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function updateTransaction(transaction, options = {}) {
  if (!transaction || !transaction.id) return;
  const index = state.transactions.findIndex(t => t.id === transaction.id);
  if (index >= 0) {
    state.transactions[index] = {
      ...state.transactions[index],
      ...transaction,
      updatedAt: transaction.updatedAt || new Date().toISOString()
    };
    if (!options.silent) {
      notifyListeners(['transactions']);
    }
  }
}

/**
 * Marks a transaction as deleted (tombstone).
 * @param {string} txId
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function removeTransaction(txId, options = {}) {
  if (!txId) return;
  const tx = state.transactions.find(t => t.id === txId);
  if (tx) {
    tx.deleted = true;
    tx.updatedAt = new Date().toISOString();
    if (!options.silent) {
      notifyListeners(['transactions']);
    }
  }
}

/**
 * Replaces the stock audits array.
 * @param {import('../types/index.js').StockAudit[]} audits
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function setStockAudits(audits, options = {}) {
  state.stockAudits = Array.isArray(audits) ? audits : [];
  if (!options.silent) {
    notifyListeners(['stockAudits']);
  }
}

/**
 * Inserts or updates a stock audit session.
 * @param {import('../types/index.js').StockAudit} audit
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function updateStockAudit(audit, options = {}) {
  if (!audit || !audit.id) return;
  const index = state.stockAudits.findIndex(a => a.id === audit.id);
  if (index >= 0) {
    state.stockAudits[index] = { ...state.stockAudits[index], ...audit, updatedAt: audit.updatedAt || new Date().toISOString() };
  } else {
    state.stockAudits.unshift({ ...audit, updatedAt: audit.updatedAt || new Date().toISOString() });
  }
  if (!options.silent) {
    notifyListeners(['stockAudits']);
  }
}

/**
 * Replaces stock-out master and detail collections.
 * @param {import('../types/index.js').StockOutMaster[]} masters
 * @param {import('../types/index.js').StockOutDetail[]} details
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function setStockOuts(masters, details, options = {}) {
  state.stockOutMaster = Array.isArray(masters) ? masters : [];
  state.stockOutDetails = Array.isArray(details) ? details : [];
  if (!options.silent) {
    notifyListeners(['stockOutMaster', 'stockOutDetails']);
  }
}

/**
 * Updates application settings.
 * @param {Partial<import('../types/index.js').AppSettings>} partialSettings
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function updateSettings(partialSettings, options = {}) {
  if (!partialSettings || typeof partialSettings !== 'object') return;
  state.settings = {
    ...state.settings,
    ...partialSettings
  };
  if (!options.silent) {
    notifyListeners(['settings']);
  }
}

/**
 * Sets selected product group IDs for batch actions.
 * @param {Set<string>|string[]} ids
 * @param {Object} [options={}]
 * @param {boolean} [options.silent=false]
 */
export function setSelectedInventoryIds(ids, options = {}) {
  state.selectedInventoryIds = ids instanceof Set ? ids : new Set(ids || []);
  if (!options.silent) {
    notifyListeners(['selectedInventoryIds']);
  }
}

/**
 * Sets active tab view.
 * @param {'inventory' | 'transactions' | 'audit' | 'stockOut'} tab
 */
export function setActiveTab(tab) {
  state.activeTab = tab;
  notifyListeners(['activeTab']);
}

/**
 * Sets inventory filter tab.
 * @param {'all' | 'low' | 'expired' | 'near-expiry'} filter
 */
export function setFilter(filter) {
  state.filter = filter;
  notifyListeners(['filter']);
}

/**
 * Sets filter date.
 * @param {string} date
 */
export function setFilterDate(date) {
  state.filterDate = date || '';
  notifyListeners(['filterDate']);
}

/**
 * Sets location filter.
 * @param {string} location
 */
export function setLocationFilter(location) {
  state.locationFilter = location || 'all';
  notifyListeners(['locationFilter']);
}

/**
 * Sets search query string.
 * @param {string} query
 */
export function setSearchQuery(query) {
  state.searchQuery = query || '';
  notifyListeners(['searchQuery']);
}

/**
 * Sets inventory sort column and direction.
 * @param {'name' | 'brand' | 'size' | string} column
 * @param {'asc' | 'desc'} [direction='asc']
 */
export function setInventorySort(column, direction = 'asc') {
  state.sortColumn = column || 'name';
  state.sortDirection = direction === 'desc' ? 'desc' : 'asc';
  notifyListeners(['sortColumn', 'sortDirection']);
}

/**
 * Sets active audit session ID.
 * @param {string|null} id
 */
export function setActiveAuditId(id) {
  state.activeAuditId = id || null;
  notifyListeners(['activeAuditId']);
}

/**
 * Sets active viewing item ID.
 * @param {string|null} id
 */
export function setActiveViewingId(id) {
  state.activeViewingId = id || null;
  notifyListeners(['activeViewingId']);
}

/**
 * Sets UI theme ('light' or 'dark').
 * @param {string} theme
 */
export function setTheme(theme) {
  state.theme = theme || 'light';
  notifyListeners(['theme']);
}

/**
 * Retrieves the timestamp of the last cloud sync.
 * @returns {string}
 */
export function getLastCloudSyncAt() {
  return String(state.lastCloudSyncAt || '').trim();
}

/**
 * Updates the last cloud sync timestamp.
 * @param {string} value
 */
export function setLastCloudSyncAt(value) {
  state.lastCloudSyncAt = String(value || '').trim();
  notifyListeners(['lastCloudSyncAt']);
}

// ============================================================================
// Concurrency Locks & Flags
// ============================================================================

export function getStockMutationInProgress() {
  return stockMutationInProgress;
}

export function setStockMutationInProgress(val) {
  stockMutationInProgress = Boolean(val);
}

export function getItemSaveInProgress() {
  return itemSaveInProgress;
}

export function setItemSaveInProgress(val) {
  itemSaveInProgress = Boolean(val);
}

export function getCloudSyncInProgress() {
  return cloudSyncInProgress;
}

export function setCloudSyncInProgress(val) {
  cloudSyncInProgress = Boolean(val);
}

export function getCloudSyncQueued() {
  return cloudSyncQueued;
}

export function setCloudSyncQueued(val) {
  cloudSyncQueued = Boolean(val);
}

export function getRealtimeSelfPauseUntil() {
  return realtimeSelfPauseUntil;
}

export function setRealtimeSelfPauseUntil(timestamp) {
  realtimeSelfPauseUntil = Number(timestamp) || 0;
}

export function getIsInventoryDirty() {
  return isInventoryDirty;
}

export function setIsInventoryDirty(val) {
  isInventoryDirty = Boolean(val);
}

export function getIsTransactionsDirty() {
  return isTransactionsDirty;
}

export function setIsTransactionsDirty(val) {
  isTransactionsDirty = Boolean(val);
}

export function getIsAuditDirty() {
  return isAuditDirty;
}

export function setIsAuditDirty(val) {
  isAuditDirty = Boolean(val);
}

export function getIsStockOutDirty() {
  return isStockOutDirty;
}

export function setIsStockOutDirty(val) {
  isStockOutDirty = Boolean(val);
}
